import {debounceTime, distinctUntilChanged, finalize, takeUntil} from 'rxjs/operators';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, OnDestroy } from '@angular/core';
import {FormControl, UntypedFormControl} from '@angular/forms';
import {Observable, Subject} from 'rxjs';
import { CampaignsService } from '../../services/campaigns.service';
import { LineItem } from '../../domain/campaigns.model';
import { SnackBarService } from 'src/app/core/services/snackbar.service';
import { SortUtils } from '../../utils/sort-utils';
import { ServerErrorUtils } from '../../utils/server-error-utils';

@Component({
  selector: 'ctv-line-items-autocomplete',
  template: ` <mat-form-field
  [floatLabel]="floatLabel || 'always'"
  class="w-100"
  [ngClass]="formFieldClass"
  [appearance]="formFieldAppearance ? formFieldAppearance : undefined"
>
  <mat-label>{{label ? label : 'Select line item'}}</mat-label>
  <input
    matInput
    #search
    aria-label="Line item"
    [matAutocomplete]="auto"
    [formControl]="filterCtrl"
    [placeholder]="placeholder ? placeholder : 'Search'"
    [required]="required"
  />
  <i class="far fa-spin fa-spinner text-primary" matSuffix [hidden]="!loading"></i>
  <mat-autocomplete
    #auto="matAutocomplete"
    (optionSelected)="optionSelected($event)"
    [displayWith]="display"
  >
    <mat-option *ngFor="let item of filteredItems | async" [value]="item">
      {{ item.name }}
    </mat-option>
  </mat-autocomplete>
</mat-form-field>`,
})
export class LineItemsAutocompleteComponent implements OnInit, OnDestroy {
  @ViewChild('search', { static: true }) searchFilter: ElementRef;
  private readonly onDestroy = new Subject<void>();
  @Input() public placeholder: string;
  @Input() public floatLabel: string;
  @Input() public label: string;
  @Input() public formFieldClass: string;
  @Input() public formFieldAppearance: string;
  @Input() required: boolean;
  @Input() preselectedLineItemId: LineItem['id'];
  @Output() lineItemSelected = new EventEmitter<LineItem>();

  filteredItems: Subject<LineItem[]>; //List of Line items to show in dropdown filtered with 'filteredLineItemIds'
  filterCtrl = new UntypedFormControl();
  lineItemList: LineItem[] = []; //stores currently fetched list of line items
  selectedLineItem: LineItem; //currently selected line item
  filteredLineItemIds: string[] = []; //It stores all the Id's of selected line items
  public loading = false;

  constructor(
    private readonly service: CampaignsService,
    private readonly snackBarService: SnackBarService) {
    this.filteredItems = new Subject();
    this.filterCtrl.valueChanges
      .pipe(debounceTime(800), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(
        (searchTerm) => {
          if (searchTerm && typeof searchTerm === 'string') {
            this.getPublishers(searchTerm);
          }
          else if (searchTerm == '') { //triggers when user deletes everything from input
            this.filteredItems.next();
            this.lineItemSelected.emit(null);
          }
        }
      );
  }

  ngOnInit(): void {
    if (this.preselectedLineItemId) {
      this.getPublishers('', this.preselectedLineItemId);
    }
  }

  /** SearchTerm used for filtering by line item name. PreselectedLineItemId used to get only one line item (preselected) at the component initialization. */
  private getPublishers(searchTerm?: string, preselectedLineItemId?: string) {
    this.loading = true;
    this.filterCtrl.disable();
    return this.service.getLineItemsListFiltered({
      page: 0,
      size: 1000,
      name: searchTerm,
      ids: [preselectedLineItemId]
    })
      .pipe(
        finalize(() => {
          this.filterCtrl.enable();
          !preselectedLineItemId ? this.searchFilter.nativeElement.focus() : 0; //No need for focus when loading page with preselected line item
          this.loading = false;
        }),
        takeUntil(this.onDestroy)
      )
      .subscribe(
        (resp) => {
          this.lineItemList = resp.content.sort(SortUtils.propertyComparatorString('name'));
          if (preselectedLineItemId) {
            this.selectedLineItem = this.lineItemList[0]; //Naturally since the api call is filtered with one ID, selectedLineItem will actually be the first one from the response
            this.filterCtrl.setValue(this.selectedLineItem);
            this.updateFilteredLineItems([this.selectedLineItem]);
            this.emit();
          }
          this.filteredItems.next(this.lineItemList.filter(x => !this.filteredLineItemIds.includes(x.id)));
        },
        (error) => {
          const messages = ServerErrorUtils.getValidationMessages(error);
          if (messages) {
            messages.forEach((m) => this.snackBarService.error(m));
          } else {
            this.snackBarService.error('Error while fetching line item autocomplete data');
          }
        },
      );
  }

  public optionSelected($event) {
    this.selectedLineItem = $event.option.value;
    this.emit();
    this.searchFilter.nativeElement.blur(); //blur == unfocus
    this.filteredItems.next(); //Reset list since the input value of autocomplete is empty and there is no point in showing filtered list on next focus on element
  }

  /** Emits value back to parent component.
   * If wrapped inside chips autocomplete it emits value to chips component that forwards values to parent component. */
  private emit() {
    this.lineItemSelected.emit(this.selectedLineItem);
  }

  /** Trigger only when using chips autocomplete. Triggered when item selected through chips autocomplete to update filteredLineItemIds. */
  public updateFilteredLineItems(items) {
    this.filteredLineItemIds = items.map((item) => item.id);
  }

  /** Returns wanted object to input from whole selected item model. */
  public display(p?: LineItem) {
    return p ? p.name : undefined;
  }

  /** Used only by chips autocomplete component to clear the input from selected item*/
  public resetInput() {
    this.filterCtrl.setValue(null);
    this.filteredItems.next(); //Reset list since the input value of autocomplete is empty
  }

  public disable() {
    this.filterCtrl.disable();
  }

  public enable() {
    this.filterCtrl.enable();
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
  }
}
